import { Box, Chip, Skeleton, Typography } from '@mui/material';
import zeplinColor from 'theme/Color';
import React, { useReducer, useState } from 'react';
import { LightTooltip } from 'components/shared/CustomTooltip/CustomTooltip';
import CustomPopOver from 'components/shared/CustomPopOver/CustomPopOver';
import CustomDeleteModal from 'components/shared/CustomDeleteModal/CustomDeleteModal';
import { modalReducer } from 'reducers/modalReducer';
import styles from './LeadsKanbanCard.module.css';
import { Lead, LeadKanbanColumnId } from '../LeadsKanbanColumn';
import { ReactComponent as VerticalDots } from 'assets/vertical-dots.svg';
import { ReactComponent as ViewIcon } from 'assets/view.svg';
import { ReactComponent as CopyIcon } from 'assets/copy-link.svg';
import { ReactComponent as ConvertClientIcon } from 'assets/convert-client.svg';
import { ReactComponent as ChangeStatusIcon } from 'assets/change-status.svg';
import { ReactComponent as ArrowIcon } from 'assets/arrow-next.svg';
import DeleteIcon from 'assets/delete-red.png';
import EditIcon from 'assets/edit.svg';

type LeadsKanbanCardProps = {
  lead?: Lead;
  columnId: LeadKanbanColumnId;
  onDragStart: (e: React.DragEvent, leadId: string, sourceColumn: LeadKanbanColumnId) => void;
  onStatusChange?: (leadId: string, status: LeadKanbanColumnId) => void;
  onDelete?: (leadId: string, onComplete?: () => void) => void;
  isDeleting?: boolean;
  isLoading?: boolean;
};

export const LeadsKanbanCard = ({
  lead,
  columnId,
  onDragStart,
  onStatusChange,
  onDelete,
  isDeleting,
  isLoading,
}: LeadsKanbanCardProps) => {
  const [anchorEl, setAnchorEl] = useState<SVGSVGElement | null>(null);
  const [statusAnchorEl, setStatusAnchorEl] = useState<HTMLDivElement | null>(null);
  const [modalState, modalDispatch] = useReducer(modalReducer, {});
  const open = Boolean(anchorEl);
  const statusOpen = Boolean(statusAnchorEl);
  const id = open ? 'lead-popover' : undefined;

  const statusOptions: LeadKanbanColumnId[] = [
    'open',
    'contacted',
    'no-response',
    'responded',
    'qualified',
    'unqualified',
  ];
  const statusLabels: Record<LeadKanbanColumnId, string> = {
    open: 'Open',
    contacted: 'Contacted',
    'no-response': 'No Response',
    responded: 'Responded',
    qualified: 'Qualified',
    unqualified: 'Unqualified',
  };

  const handleClick = (event: React.MouseEvent<SVGSVGElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleViewDetails = (e: React.MouseEvent) => {
    e.stopPropagation();
    // TODO: Implement view details functionality
    console.log('View details:', lead?.leadId);
    setAnchorEl(null);
  };

  const handleEditLead = (e: React.MouseEvent) => {
    e.stopPropagation();
    // TODO: Implement edit lead functionality
    console.log('Edit lead:', lead?.leadId);
    setAnchorEl(null);
  };

  const handleConvertToClient = (e: React.MouseEvent) => {
    e.stopPropagation();
    // TODO: Implement convert to client functionality
    console.log('Convert to client:', lead?.leadId);
    setAnchorEl(null);
  };

  const handleChangeStatus = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    // Open status selection popover on the right
    setStatusAnchorEl(e.currentTarget);
  };

  const handleSelectStatus = (status: LeadKanbanColumnId) => {
    if (onStatusChange && lead?.leadId) {
      onStatusChange(lead.leadId, status);
    }
    setStatusAnchorEl(null);
    setAnchorEl(null);
  };

  const handleCopyLink = (e: React.MouseEvent) => {
    e.stopPropagation();
    // TODO: Implement copy link functionality
    const leadLink = `${window.location.origin}/leads/${lead?.leadId}`;
    navigator.clipboard.writeText(leadLink).then(() => {
      console.log('Link copied to clipboard');
    });
    setAnchorEl(null);
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    modalDispatch({ type: 'OPEN', payload: 'delete' });
    setAnchorEl(null);
  };

  const handleConfirmDelete = () => {
    if (onDelete && lead?.leadId) {
      onDelete(lead.leadId, () => {
        modalDispatch({ type: 'CLOSE', payload: 'delete' });
      });
    }
  };

  if (isLoading) {
    return (
      <Box
        sx={{
          border: `1px solid ${zeplinColor.Neutral5}`,
          borderRadius: '14px',
          padding: '16px',
          display: 'flex',
          flexDirection: 'column',
          gap: '16px',
          minWidth: '292px',
        }}
      >
        <Box sx={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
          <Skeleton variant='circular' width={52} height={52} />
          <Box sx={{ flex: 1 }}>
            <Skeleton variant='text' width='70%' height={20} />
            <Skeleton variant='text' width='50%' height={18} />
          </Box>
        </Box>
        <Skeleton variant='rounded' width='60%' height={24} />
        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: '8px' }}>
          <Skeleton variant='text' width='45%' height={18} />
          <Skeleton variant='text' width='45%' height={18} />
        </Box>
      </Box>
    );
  }

  return (
    <Box
      draggable='true'
      onDragStart={(e) => lead?.leadId && onDragStart(e, lead.leadId, columnId)}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        border: `1px solid ${zeplinColor.Neutral5}`,
        borderRadius: '14px',
        padding: '16px',
        gap: '20px',
        minWidth: '292px',
        ':hover': {
          padding: '15px',
          border: `2px solid ${zeplinColor.BasicBlack}`,
        },
      }}
    >
      <Box sx={{ display: 'flex', gap: '12px' }}>
        <img
          src={lead?.profileImageUrl ?? `/assets/default-avatar.png`}
          onError={(e) => {
            e.currentTarget.src = `/assets/slack-user.svg`;
          }}
          alt={lead?.name}
          width={52}
          height={52}
          style={{ borderRadius: '48px' }}
        />
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px', width: '100%' }}>
          <Box
            sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flex: 1 }}
          >
            <Typography variant='heading4' sx={{ color: zeplinColor.BasicBlack }}>
              {lead?.name}
            </Typography>
            <LightTooltip
              title={
                <Typography variant='responsiveBody3' color={zeplinColor.Neutral1}>
                  Options
                </Typography>
              }
              placement='bottom-end'
              style={{ backgroundColor: zeplinColor.BasicBlack, borderRadius: '8px' }}
              // sx={{ [`& .${tooltipClasses.tooltip}`]: { backgroundColor: zeplinColor.Neutral1 } }}
            >
              <VerticalDots
                className={styles['vertical-icon']}
                onClick={handleClick}
                style={{ cursor: 'pointer' }}
              />
            </LightTooltip>
          </Box>
          <Box sx={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <Chip
              label={
                <Typography variant='responsiveLabel1' color={zeplinColor.Neutral1}>
                  {lead?.sport ?? 'N/A'}
                </Typography>
              }
              sx={{
                backgroundColor: zeplinColor.BasicBlack,
                padding: '5px 12px 5px 12px',
                borderRadius: '50px',
                // width: '47px',
                height: '24px',
                '&.MuiChip-root': {
                  // flex: 1,
                },
              }}
            />
            <Box
              sx={{
                height: '16px',
                width: '1px',
                backgroundColor: zeplinColor.Neutral5,
                display: 'inline-block',
                marginX: '4px', // Add some margin on both sides
              }}
            />
            <Typography variant='body3' sx={{ color: zeplinColor.BasicBlack }}>
              {lead?.agencyName ?? 'N/A'}
            </Typography>
          </Box>
        </Box>
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: '8px' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <img src={`/assets/user-single.svg`} alt={lead?.createdBy} width={18} height={18} />
          <Typography variant='body3' sx={{ color: zeplinColor.BasicBlack }}>
            {lead?.createdBy}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <img src={`/assets/calendar.svg`} alt={lead?.createdAt} width={18} height={18} />
          <Typography variant='body3' sx={{ color: zeplinColor.BasicBlack }}>
            {lead?.createdAt
              ? new Date(lead.createdAt).toLocaleDateString('en-US', {
                  month: 'numeric',
                  day: 'numeric',
                  year: 'numeric',
                })
              : ''}
          </Typography>
        </Box>
      </Box>

      <CustomPopOver
        onClick={(e: any) => {
          e.stopPropagation();
        }}
        setAnchorEl={setAnchorEl}
        anchorEl={anchorEl}
        id={'simple-popover'}
        sx={{ borderRadius: '14px', boxShadow: 'rgba(0, 0, 0, 0.15)' }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '16px',
            padding: '16px',
          }}
        >
          <Box
            sx={{ display: 'flex', gap: '9px', cursor: 'pointer' }}
            onClick={handleConvertToClient}
          >
            <ConvertClientIcon width={18} height={18} />
            <Typography variant='responsiveBody3' color={zeplinColor.BasicBlack}>
              Convert to client
            </Typography>
          </Box>
          <Box
            sx={{
              display: 'flex',
              gap: '9px',
              cursor: 'pointer',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
            onClick={handleChangeStatus}
          >
            <Box sx={{ display: 'flex', gap: '9px', alignItems: 'center' }}>
              <ChangeStatusIcon width={18} height={18} />
              <Typography variant='responsiveBody3' color={zeplinColor.BasicBlack}>
                Change status
              </Typography>
            </Box>
            <ArrowIcon width={16} height={16} />
          </Box>
          <Box sx={{ display: 'flex', gap: '9px', cursor: 'pointer' }} onClick={handleViewDetails}>
            <ViewIcon width={18} height={18} />
            <Typography variant='responsiveBody3' color={zeplinColor.BasicBlack}>
              View details
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: '9px', cursor: 'pointer' }} onClick={handleEditLead}>
            <img src={EditIcon} width={18} height={18} alt='edit' />
            <Typography variant='responsiveBody3' color={zeplinColor.BasicBlack}>
              Edit lead
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', gap: '9px', cursor: 'pointer' }} onClick={handleCopyLink}>
            <CopyIcon width={18} height={18} />
            <Typography variant='responsiveBody3' color={zeplinColor.BasicBlack}>
              Copy link
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: '9px', cursor: 'pointer' }} onClick={handleDelete}>
            <img src={DeleteIcon} width={18} height={18} alt='delete' />
            <Typography variant='responsiveBody3' color={zeplinColor.Error}>
              Delete
            </Typography>
          </Box>
        </Box>
      </CustomPopOver>

      {/* Status Selection Popover */}
      <CustomPopOver
        onClick={(e: any) => {
          e.stopPropagation();
        }}
        setAnchorEl={setStatusAnchorEl}
        anchorEl={statusAnchorEl}
        id={undefined}
        sx={{ borderRadius: '14px', boxShadow: 'rgba(0, 0, 0, 0.15)', right: '-1rem' }}
        horizontal='right'
        vertical='top'
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
            padding: '12px 0px',
          }}
        >
          {statusOptions.map((status) => (
            <Box
              key={status}
              sx={{
                display: 'flex',
                gap: '12px',
                cursor: 'pointer',
                padding: '8px 16px',
                alignItems: 'center',

                '&:hover': {
                  backgroundColor: zeplinColor.Neutral5,
                },
              }}
              onClick={(e) => {
                e.stopPropagation();
                handleSelectStatus(status);
              }}
            >
              <Box
                sx={{
                  width: '8px',
                  height: '8px',
                  borderRadius: '50%',
                  backgroundColor:
                    lead?.status?.toLowerCase().replace(/\s+/g, '-') === status
                      ? zeplinColor.BasicBlack
                      : 'transparent',
                }}
              />
              <Typography variant='responsiveBody3' color={zeplinColor.BasicBlack}>
                {statusLabels[status]}
              </Typography>
            </Box>
          ))}
        </Box>
      </CustomPopOver>

      {/* Delete Modal */}
      <CustomDeleteModal
        isModalOpen={modalState.delete || false}
        modalDispatch={modalDispatch}
        onDelete={handleConfirmDelete}
        title='Delete Lead?'
        itemName={lead?.name}
        description={`Are you sure you want to delete ${lead?.name}? This action cannot be undone.`}
        isDeleting={isDeleting}
      />
    </Box>
  );
};
